import { Link } from 'react-router-dom';

const wissenCategories = [
  { id: 'neurodiversitaet', title: 'Neurodiversität', description: 'Grundlagen und Verständnis neurodiverser Denkweisen.', icon: '🧠' },
  { id: 'ads-adhs', title: 'ADS / ADHS', description: 'Aufmerksamkeit, Konzentration und individuelle Stärken.', icon: '⚡' },
  { id: 'autismus', title: 'Autismus', description: 'Spektrum, Wahrnehmung und besondere Fähigkeiten.', icon: '🌈' },
  { id: 'jugend-familie', title: 'Jugend & Familie', description: 'Begleitung, Unterstützung und Entwicklung im Alltag.', icon: '👨‍👩‍👧' },
  { id: 'schule-ausbildung', title: 'Schule & Ausbildung', description: 'Bildungswege, Herausforderungen und Möglichkeiten.', icon: '📚' },
  { id: 'arbeit-unternehmen', title: 'Arbeit & Unternehmen', description: 'Berufliche Integration und Potenziale im Arbeitsmarkt.', icon: '🏢' },
  { id: 'forschung', title: 'Forschung', description: 'Aktuelle Studien, Erkenntnisse und Entwicklungen.', icon: '🔬' },
  { id: 'teilhabe', title: 'Gesellschaftliche Teilhabe', description: 'Inklusion, Akzeptanz und gemeinsames Miteinander.', icon: '🤝' },
];

export default function WissenPage() {
  return (
    <div className="bg-white" style={{ fontFamily: 'Inter, -apple-system, sans-serif' }}>
      {/* Hero */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-[#FAF9F7] to-white">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
              Wissen
            </span>
            <h1 className="text-4xl md:text-[52px] font-bold text-gray-900 leading-tight tracking-tight mb-6">
              Verstehen. Entwickeln. Handeln.
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Ein Denkraum für alle, die Neurodiversität besser verstehen möchten – 
              Impulse, Erkenntnisse und Grundlagen für Familien, Fachleute und Interessierte.
            </p>
          </div>
        </div>
      </section>

      {/* Kategorien */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {wissenCategories.map((category) => (
              <div 
                key={category.id}
                className="bg-white border border-[#E8E4DE] rounded-2xl p-8 hover:border-[#C4A962] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              >
                <div className="text-4xl mb-5">{category.icon}</div>
                <h3 className="font-bold text-lg text-gray-900 mb-3">{category.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{category.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Grundverständnis */}
      <section className="py-24 md:py-32 bg-[#FAF9F7]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
                Grundverständnis
              </span>
              <h2 className="text-3xl md:text-[38px] font-bold text-gray-900 tracking-tight mb-6">
                Was ist Neurodiversität?
              </h2>
              <div className="text-[17px] text-gray-600 leading-relaxed space-y-4">
                <p>
                  Neurodiversität beschreibt die natürliche Vielfalt menschlicher Gehirne und Denkweisen. 
                  Sie umfasst unterschiedliche neurologische Funktionsweisen wie Autismus, ADHS, Legasthenie und andere.
                </p>
                <p>
                  <strong className="text-gray-900">Neurodiversität ist keine Krankheit</strong> – sie ist Teil 
                  menschlicher Vielfalt, wie auch Unterschiede in Größe, Aussehen oder kulturellem Hintergrund.
                </p>
                <p>
                  Menschen aus dem neurodiversen Spektrum bringen oft besondere Fähigkeiten, ungewöhnliche Perspektiven 
                  und kreative Denkansätze mit, die unsere Gesellschaft bereichern.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/d4ed44b3-63d8-417d-957c-4836bdf688a4/images/e7e1889888dff6200a75f866ad5deec6c76f9909e26db3337e163f5b544e8ce7.jpeg" 
                alt="Junge Menschen beim Lernen und Austausch"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Kernaussagen */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
              Unsere Perspektive
            </span>
            <h2 className="text-3xl md:text-[42px] font-bold text-gray-900 tracking-tight">
              Was wir vermitteln möchten.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { text: 'Die Diagnose beschreibt einen Teil eines Menschen – sie definiert nicht seinen Wert.', label: 'Identität' },
              { text: 'Jeder Mensch hat Fähigkeiten, die erkannt und gefördert werden können.', label: 'Potenzial' },
              { text: 'Unterschiedliche Denkweisen sind eine Bereicherung, keine Einschränkung.', label: 'Vielfalt' },
              { text: 'Manchmal brauchen Menschen nicht mehr Anpassung, sondern ein passendes Umfeld.', label: 'Umfeld' },
              { text: 'Förderung bedeutet Möglichkeiten schaffen – nicht nur Defizite ausgleichen.', label: 'Förderung' },
              { text: 'Teilhabe und Entwicklung sind möglich, wenn wir die richtigen Zugänge öffnen.', label: 'Zukunft' },
            ].map((item, i) => (
              <div 
                key={i}
                className="bg-[#F5F3F0] rounded-xl p-8 border-l-4 border-[#C4A962] hover:bg-white hover:shadow-md transition-all duration-300"
              >
                <p className="text-gray-900 leading-relaxed mb-4">„{item.text}"</p>
                <p className="text-xs font-semibold text-[#A8923F] uppercase tracking-wider">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* In Vorbereitung */}
      <section className="py-24 md:py-32 bg-[#FAF9F7]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="bg-white rounded-2xl p-12 border border-[#E8E4DE] text-center max-w-2xl mx-auto">
            <div className="text-5xl mb-6">📚</div>
            <h3 className="font-bold text-xl text-gray-900 mb-4">Weitere Inhalte in Vorbereitung</h3>
            <p className="text-gray-600 mb-6">
              Wir arbeiten an weiteren Artikeln, Studienhinweisen und Impulsen. 
              Kontaktieren Sie uns, wenn Sie zu einem bestimmten Thema mehr erfahren möchten.
            </p>
            <Link 
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-md font-medium transition-colors"
            >
              Kontakt aufnehmen
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
